import jsPDF from "jspdf";
import "jspdf-autotable";
import config from "../common/Config";
import axios from "axios";

const formatMoneyPlain = (amount) => parseFloat(amount || 0).toFixed(2);

const getItemVatCode = (item) =>
  String(item.vat_code ?? item.tax_code ?? item.tax_id ?? "") || "-";

const getItemTax = (item) =>
  parseFloat(item.tax ?? item.item_tax ?? item.vat_amount ?? 0) || 0;

const getItemSubtotal = (item) => parseFloat(item.subtotal ?? 0) || 0;

const getItemNet = (item) => {
  const subtotal = getItemSubtotal(item);
  const tax = getItemTax(item);
  const explicitNet = parseFloat(item.net ?? item.net_amount ?? NaN);
  if (Number.isFinite(explicitNet)) return explicitNet;
  if (subtotal >= tax) return subtotal - tax;
  return subtotal;
};

const buildVatAnalysis = (items = []) => {
  const groups = new Map();
  items.forEach((item) => {
    const code = String(item.vat_code ?? item.tax_code ?? item.tax_id ?? "");
    const rate = parseFloat(
      item.vat_rate ?? item.tax_rate_percent ?? item.tax_rate ?? 0
    );
    const safeRate = Number.isFinite(rate) ? rate : 0;
    const name =
      item.vat_name ||
      item.tax_name ||
      (safeRate === 0 ? "Zero Rated" : "Std Rate");
    const key = `${code}|${safeRate}|${name}`;
    if (!groups.has(key)) {
      groups.set(key, {
        code: code || "-",
        rate: safeRate,
        name,
        net: 0,
        vat: 0,
      });
    }
    const row = groups.get(key);
    row.net += getItemNet(item);
    row.vat += getItemTax(item);
  });
  return Array.from(groups.values()).sort((a, b) =>
    String(a.code).localeCompare(String(b.code), undefined, { numeric: true })
  );
};

const formatDate = (timestamp) => {
  if (!timestamp) return "";
  const date = new Date(timestamp);
  if (Number.isNaN(date.getTime())) {
    const raw = String(timestamp);
    const match = raw.match(/^(\d{4})-(\d{2})-(\d{2})/);
    if (match) return `${match[3]}-${match[2]}-${match[1]}`;
    return raw;
  }
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${day}-${month}-${year}`;
};

const drawLabelValue = (doc, label, value, labelX, valueX, yPos, maxWidth) => {
  doc.setFont("helvetica", "bold");
  doc.text(label, labelX, yPos);
  doc.setFont("helvetica", "normal");
  const text = String(value ?? "");
  if (maxWidth) {
    const lines = doc.splitTextToSize(text, maxWidth);
    doc.text(lines, valueX, yPos);
    return lines.length;
  }
  doc.text(text, valueX, yPos);
  return 1;
};

const generateReturnPDFRhp = async (returnId) => {
  const accessToken = localStorage.getItem("accessToken");
  const formData = new FormData();
  formData.set("id", returnId);
  const apiUrl = `${config.API_URL}/return/returnView`;

  try {
    const response = await axios.post(apiUrl, formData, {
      headers: {
        authorization: `Bearer ${accessToken}`,
      },
    });

    const data = response.data;
    if (!data.success || !data.data) return;

    const shopDetails = data.data.shop_details || {};
    const commonData = data.data.commonData || {};
    const itemData = Array.isArray(data.data.itemData) ? data.data.itemData : [];

    const vatRows = buildVatAnalysis(itemData);
    const totalNet = vatRows.reduce((sum, row) => sum + row.net, 0);
    const totalVat = vatRows.reduce((sum, row) => sum + row.vat, 0);
    const totalGross =
      parseFloat(commonData.grand_total) > 0
        ? parseFloat(commonData.grand_total)
        : totalNet + totalVat;

    const doc = new jsPDF("portrait");
    doc.setTextColor(0, 0, 0);
    const pageWidth = doc.internal.pageSize.getWidth();
    const pageHeight = doc.internal.pageSize.getHeight();
    const margin = 10;
    let y = 12;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(11);
    doc.text(String(shopDetails.shop_name || ""), margin, y + 4);

    doc.setFontSize(16);
    doc.text("Credit Note", pageWidth - margin, y + 4, { align: "right" });

    y += 14;
    doc.setFontSize(9);
    doc.setFont("helvetica", "normal");
    const infoLabelX = pageWidth - 100;
    const leftBlockMaxWidth = infoLabelX - margin - 8;
    const shopAddress = String(shopDetails.address || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
    shopAddress.forEach((line) => {
      const wrappedLines = doc.splitTextToSize(String(line), leftBlockMaxWidth);
      doc.text(wrappedLines, margin, y);
      y += wrappedLines.length * 4.5;
    });
    doc.setFont("helvetica", "bold");
    doc.text(
      `VAT Number: ${shopDetails.vat_no || shopDetails.company_reg_no || ""}`,
      margin,
      y
    );
    y += 8;

    const customerLines = [
      commonData.company,
      commonData.line1 || commonData.address,
      commonData.line2,
      commonData.city,
      commonData.postal_code,
      commonData.country,
    ].filter((line) => String(line || "").trim());

    doc.setFont("helvetica", "normal");
    customerLines.forEach((line) => {
      const wrappedLines = doc.splitTextToSize(String(line), leftBlockMaxWidth);
      doc.text(wrappedLines, margin, y);
      y += wrappedLines.length * 4.5;
    });

    const infoValueX = pageWidth - 72;
    const rightColX = pageWidth - margin;
    const infoValueMaxWidth = rightColX - infoValueX - 12;
    const infoLineHeight = 4.5;
    let infoY = 26;

    const infoRows = [
      ["For Return", commonData.reference_no || ""],
      ["Dated", formatDate(commonData.date)],
      ["All Amounts in", "Pounds"],
      ["Terms", commonData.terms || "30 days from document date"],
      [
        "Current Balance",
        formatMoneyPlain(
          commonData.current_balance ?? commonData.balance ?? ""
        ),
      ],
      ["Your VAT Number", commonData.customer_vat_no || commonData.vat_no || ""],
    ];

    const rightColumnByRow = {
      4:
        commonData.return_by ||
        commonData.created_by ||
        commonData.user_name ||
        "",
      5: "-",
    };

    infoRows.forEach(([label, value], index) => {
      const lineCount = drawLabelValue(
        doc,
        label,
        value,
        infoLabelX,
        infoValueX,
        infoY,
        infoValueMaxWidth
      );
      if (rightColumnByRow[index] !== undefined) {
        doc.setFont("helvetica", "normal");
        doc.text(String(rightColumnByRow[index]), rightColX, infoY, {
          align: "right",
        });
      }
      infoY += Math.max(5, lineCount * infoLineHeight);
    });

    y = Math.max(y, infoY) + 6;

    const tableBody = itemData.map((item, index) => [
      index + 1,
      item.product_code || "",
      item.product_name || "",
      parseInt(item.quantity, 10) || 0,
      String(item.return_type || "Box"),
      getItemVatCode(item),
      formatMoneyPlain(getItemNet(item)),
    ]);

    const totalWidth = pageWidth - 2 * margin;

    doc.autoTable({
      startY: y,
      head: [
        ["Line", "Item Code", "Description", "Quantity", "Unit", "VAT Code", "Net"],
      ],
      body: tableBody,
      theme: "plain",
      styles: {
        textColor: [0, 0, 0],
        lineWidth: 0,
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontSize: 9,
        fontStyle: "bold",
        valign: "middle",
        halign: "center",
        lineWidth: 0,
      },
      bodyStyles: {
        fontSize: 8,
        textColor: [0, 0, 0],
        valign: "middle",
        lineWidth: 0,
      },
      alternateRowStyles: {
        fillColor: [255, 255, 255],
      },
      margin: { left: margin, right: margin, bottom: 55 },
      columnStyles: {
        0: { cellWidth: totalWidth * 0.06, halign: "center" },
        1: { cellWidth: totalWidth * 0.12, halign: "left" },
        2: { cellWidth: totalWidth * 0.36, halign: "left" },
        3: { cellWidth: totalWidth * 0.1, halign: "center" },
        4: { cellWidth: totalWidth * 0.1, halign: "center" },
        5: { cellWidth: totalWidth * 0.12, halign: "center" },
        6: { cellWidth: totalWidth * 0.14, halign: "right" },
      },
    });

    const lastPage = doc.internal.getNumberOfPages();
    doc.setPage(lastPage);

    const bottomPadding = 30;
    const vatDataLineHeight = 5;
    const grossY = pageHeight - bottomPadding;
    const lastVatDataY =
      vatRows.length > 1
        ? grossY
        : grossY - vatDataLineHeight;
    const vatDataStartY =
      lastVatDataY - Math.max(vatRows.length - 1, 0) * vatDataLineHeight;
    const vatHeaderY = vatDataStartY - vatDataLineHeight;
    const vatTitleY = vatHeaderY - 6;
    const netSummaryY = vatHeaderY;
    const vatSummaryY = vatDataStartY;

    const vatColCode = margin;
    const vatColPct = margin + 14;
    const vatColName = margin + 30;
    const vatColNet = margin + 70;
    const vatColVat = margin + 90;

    doc.setFont("helvetica", "bold");
    doc.setFontSize(9);
    doc.text(
      "-----------------VAT Analysis-----------------",
      margin + 48,
      vatTitleY,
      { align: "center" }
    );

    doc.text("Code", vatColCode, vatHeaderY);
    doc.text("%", vatColPct, vatHeaderY);
    doc.text("Net", vatColNet, vatHeaderY);
    doc.text("VAT", vatColVat, vatHeaderY);

    doc.setFont("helvetica", "normal");
    let vatDataY = vatDataStartY;
    vatRows.forEach((row) => {
      doc.text(String(row.code), vatColCode, vatDataY);
      doc.text(formatMoneyPlain(row.rate), vatColPct, vatDataY);
      doc.text(String(row.name || ""), vatColName, vatDataY);
      doc.text(formatMoneyPlain(row.net), vatColNet, vatDataY);
      doc.text(formatMoneyPlain(row.vat), vatColVat, vatDataY);
      vatDataY += vatDataLineHeight;
    });

    const summaryValueX = pageWidth - margin;
    const summaryLabelX = pageWidth - margin - 24;

    const drawSummaryRow = (label, value, y) => {
      doc.setFont("helvetica", "bold");
      doc.text(label, summaryLabelX, y);
      doc.setFont("helvetica", "normal");
      doc.text(value, summaryValueX, y, { align: "right" });
    };

    drawSummaryRow("NET", formatMoneyPlain(totalNet), netSummaryY);
    drawSummaryRow("VAT", formatMoneyPlain(totalVat), vatSummaryY);
    drawSummaryRow("GROSS", formatMoneyPlain(totalGross), grossY);

    const pageCount = doc.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      doc.setPage(i);
      doc.setFontSize(8);
      doc.setFont("helvetica", "normal");
      doc.text(
        `Page ${i} of ${pageCount}`,
        pageWidth - margin,
        pageHeight - 8,
        { align: "right" }
      );
    }

    doc.autoPrint();
    window.open(doc.output("bloburl"));
  } catch (error) {}
};

export default generateReturnPDFRhp;
